import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import HomeService from "../../../services/home.service";
import {
  MdInsertDriveFile,
  MdViewInAr,
  MdPallet,
  MdCalendarToday,
  MdShowChart,
} from "react-icons/md";

moment.locale("es");

const Home = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const timerRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(
    async (reload = false, currentSearch = search) => {
      const currentPage = reload ? 1 : page;
      setLoading(true);
      if (reload) setPage(1);

      try {
        const res = await HomeService.admin.get({
          page: currentPage,
          withoutLoading: true,
          search: currentSearch,
        });
        setData((prev) =>
          reload ? res.packages.rows : [...prev, ...res.packages.rows],
        );
        setLastPage(res.packages.count);
      } finally {
        setLoading(false);
        setLoadingInitial(false);
      }
    },
    [page, search],
  );

  useEffect(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    if (page > 1) load();
  }, [page, load]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(true, val), 1000);
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.2 &&
      page < lastPage &&
      !loading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div
      className="flex flex-col h-screen overflow-y-auto bg-[#F8F6FD]"
      onScroll={handleScroll}
    >
      <main className="flex-1 px-4 pt-1 pb-20">
        <button
          onClick={() => navigate("/admin/packages/finish")}
          className="flex w-full justify-end items-center px-4 mt-1 bg-transparent border-none cursor-pointer"
        >
          <MdInsertDriveFile size={18} className="text-brand-blue2" />
          <span className="ml-1 font-bold text-brand-blue2 underline">
            Ver registros finalizados
          </span>
        </button>

        <input
          type="text"
          placeholder="Buscar por BL o Código Interno"
          className="w-full border border-brand-gray7 bg-white rounded-full mt-2.5 mb-5 px-4 h-12 outline-none focus:border-brand-purple transition-colors"
          value={search}
          onChange={handleSearch}
        />

        <div className="flex flex-row items-center mb-4">
          <div className="w-9 h-9 rounded-full bg-[#EEF2FF] flex justify-center items-center mr-2.5">
            <MdViewInAr size={20} className="text-brand-purple" />
          </div>
          <div>
            <h2 className="font-bold text-base text-brand-purple uppercase m-0">
              Contenedor en tránsito
            </h2>
            <div className="h-0.5 bg-brand-purple w-7 mt-0.5" />
          </div>
        </div>

        <div ref={listRef}>
          {loadingInitial && data.length === 0 && (
            <div className="flex justify-center my-5">
              <span className="text-brand-green font-semibold">
                Cargando...
              </span>
            </div>
          )}
          {!loadingInitial && data.length === 0 && (
            <div className="text-center my-2.5 text-brand-gray3">
              No se han encontrado registros
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.map((item, index) => (
              <div
                key={index}
                className="bg-white shadow-md p-4 rounded-xl border border-gray-100 flex flex-col justify-between"
              >
                <div>
                  <div className="flex flex-row justify-between mb-2.5">
                    <div className="flex flex-row items-center flex-1">
                      <div className="w-11 h-11 rounded-full bg-[#EEF2FF] flex justify-center items-center mr-3 shrink-0">
                        <MdPallet size={24} className="text-brand-purple" />
                      </div>
                      <div className="flex-1 truncate pr-2">
                        <div className="font-bold text-[15px] text-brand-black truncate">
                          {item.code || "AAA"}
                        </div>
                        {item.internal_code && (
                          <div className="text-[13px] text-gray-500 my-[1px] truncate">
                            {item.internal_code}
                          </div>
                        )}
                        <div className="font-bold text-brand-purple text-[13px] truncate">
                          {item.number}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row items-start shrink-0">
                      <MdCalendarToday
                        size={16}
                        className="text-brand-purple"
                      />
                      <span className="ml-1 text-xs text-gray-500">
                        {item.created_at &&
                          moment(
                            item.last_activity_date || item.created_at,
                          ).format("DD MMM. YYYY")}
                      </span>
                    </div>
                  </div>

                  <div className="mb-1">
                    <p className="text-sm text-brand-black mb-1 leading-5 m-0">
                      <span className="font-bold">Último Movimiento:</span>{" "}
                      {item.last_activity}
                    </p>
                    <p className="text-sm text-brand-black mb-1 leading-5 m-0">
                      <span className="font-bold">Ubicación:</span>{" "}
                      {item.location}
                    </p>
                  </div>
                </div>

                <div>
                  <hr className="border-0 h-[1px] bg-brand-gray7 my-3" />

                  <div className="flex flex-row justify-evenly items-center">
                    <button
                      className="flex flex-row flex-1 justify-center items-center py-1 bg-transparent border-none cursor-pointer text-brand-green hover:opacity-80 transition-opacity"
                      onClick={() =>
                        navigate("/admin/packages/view-activity", {
                          state: { item },
                        })
                      }
                    >
                      <MdShowChart size={18} />
                      <span className="font-bold ml-2 text-sm">
                        Ver actividad
                      </span>
                    </button>
                    <div className="w-[1px] h-5 bg-brand-gray7" />
                    <button
                      className="flex flex-row flex-1 justify-center items-center py-1 bg-transparent border-none cursor-pointer text-brand-blue hover:opacity-80 transition-opacity"
                      onClick={() =>
                        navigate("/admin/packages/view-content", {
                          state: { item },
                        })
                      }
                    >
                      <MdViewInAr size={18} />
                      <span className="font-bold ml-2 text-sm">
                        Ver contenido
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {loading && lastPage > 1 && (
            <div className="flex justify-center my-5 text-brand-green font-semibold">
              Cargando más...
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Home;
